import * as React from 'react';

import { ThemedStyleSheet, useCurrentTheme } from '../theme';
import {
    normalizeStyle,
    type GestureResponderEvent,
    type StyleProp,
    type TextProps,
    type TextStyle,
} from '../web';

export type LinkKind = 'default' | 'in-text' | 'in-text-muted' | 'blank' | 'bold';

export type LinkProps = TextProps & {
    kind?: LinkKind;

    /** URL for accessibility */
    url?: string;

    /** Custom handler for press events, it default to opening the url  */
    onPress?: (event: GestureResponderEvent) => void;

    children: React.ReactNode;
    style?: StyleProp<TextStyle>;
};

/**
 * Similar to a button but acting as inline text.
 */
export function Link({
    ref,
    ...props
}: LinkProps & { ref?: React.Ref<HTMLElement> }): React.ReactElement {
    const {
        accessibilityLabel,
        accessibilityRole,
        children,
        className,
        dataSet,
        kind = 'default',
        numberOfLines: _numberOfLines,
        onClick,
        onMouseEnter,
        onMouseLeave,
        onPress,
        onPressIn,
        onPressOut,
        selectable: _selectable,
        style,
        testID,
        url,
        ...rest
    } = props;
    const [hovered, setHovered] = React.useState(false);

    const [, styles] = useCurrentTheme(themedKindStyles[kind]);
    const commonProps = {
        ...rest,
        ref,
        className,
        role: accessibilityRole,
        'aria-label': accessibilityLabel,
        'data-testid': testID,
        ...Object.fromEntries(
            Object.entries(dataSet ?? {}).map(([key, value]) => [`data-${key}`, value])
        ),
        style: normalizeStyle([styles.link, hovered && styles.linkHovered, style]),
        onClick: (event: React.MouseEvent<HTMLElement>) => {
            onClick?.(event);
            onPress?.(event as GestureResponderEvent);
        },
        onMouseDown: (event: React.MouseEvent<HTMLElement>) => {
            onPressIn?.(event as GestureResponderEvent);
        },
        onMouseUp: (event: React.MouseEvent<HTMLElement>) => {
            onPressOut?.(event as GestureResponderEvent);
        },
        onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
            setHovered(true);
            onMouseEnter?.(event);
        },
        onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
            setHovered(false);
            onMouseLeave?.(event);
        },
    };

    if (!url) {
        return (
            <button
                {...(commonProps as React.ComponentPropsWithoutRef<'button'>)}
                type="button"
                className={`appearance-none border-0 bg-transparent p-0 text-inherit ${className ?? ''}`}
            >
                {children}
            </button>
        );
    }

    return (
        <a
            {...(commonProps as React.ComponentPropsWithoutRef<'a'>)}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
        >
            {children}
        </a>
    );
}

const themedKindStyles = {
    default: ThemedStyleSheet.create({
        link: (theme) => ({
            color: theme.colors.primary.main,
        }),
        linkHovered: (theme) => ({
            color: theme.colors.primary.standout,
        }),
    }),
    'in-text': ThemedStyleSheet.create({
        link: (theme) => ({
            textDecorationLine: 'underline',
            color: theme.colors.primary.main,
        }),
        linkHovered: () => ({
            textDecorationLine: 'none',
        }),
    }),
    'in-text-muted': ThemedStyleSheet.create({
        link: (theme) => ({
            textDecorationLine: 'underline',
            color: theme.colors.text.main,
        }),
        linkHovered: (theme) => ({
            color: theme.colors.primary.standout,
        }),
    }),
    blank: ThemedStyleSheet.create({
        link: (theme) => ({
            color: theme.colors.text.main,
        }),
        linkHovered: (theme) => ({
            color: theme.colors.primary.main,
        }),
    }),
    bold: ThemedStyleSheet.create({
        link: (theme) => ({
            color: theme.colors.text.main,
        }),
        linkHovered: (theme) => ({
            color: theme.colors.text.standout,
        }),
    }),
};
